import { createInstance, dateReviver } from '@/api';
import {
    ProductGroupClient,
    ProductGroupCreateDto,
    ProductGroupDetailDto,
    ProductGroupUpdateDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductGroupEditStore } from './product-group-edit.store';

export interface ProductGroupDetailStoreState {
    detail?: ProductGroupDetailDto;
    loading: boolean;
    reloading: boolean;
    notFound: boolean;
}

export const client = new ProductGroupClient('', createInstance(), dateReviver);

export const useProductGroupDetailStore = defineStore('configuration-product-group-detail-store', {
    state: (): ProductGroupDetailStoreState => ({
        detail: null,
        loading: false,
        reloading: false,
        notFound: false
    }),
    getters: {},
    actions: {
        async get(id: number, reloading: boolean = false) {
            reloading ? (this.reloading = true) : (this.loading = true);
            this.notFound = false;
            try {
                this.detail = await client.getDetail(id);
                return this.detail;
            } catch (error: any) {
                // A group is also reachable by typing its id in the url, so a wrong id is a normal
                // mistake here and gets its own message on the page instead of a toast.
                if (error.status === 404) {
                    this.notFound = true;
                } else {
                    useAlertStore().handleException(i18n.global.t('productgroup.detail.error.get.title'), error, false);
                }
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async create(dto: ProductGroupCreateDto): Promise<number> {
            this.loading = true;
            let response = null;
            try {
                response = await client.create(dto);
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productgroup.detail.error.create.title'), error);
            } finally {
                this.loading = false;
            }
            return response?.id;
        },
        async update(id: number, dto: ProductGroupUpdateDto) {
            this.reloading = true;
            try {
                await client.update(id, dto);
                useAlertStore().setSuccess(i18n.global.t('productgroup.detail.success.update.title'));
                await this.get(id, true);
                useProductGroupEditStore().closeEdit();
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productgroup.detail.error.update.title'), error);
            } finally {
                this.reloading = false;
            }
        },
        async delete(id: number): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(id);
                useAlertStore().setSuccess(i18n.global.t('productgroup.detail.success.delete.title'));
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('productgroup.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear() {
            this.detail = null;
            this.notFound = false;
        }
    }
});
